import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography, Stack, Box, Chip, Alert,
    CircularProgress, TextField, Autocomplete, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Checkbox,
    Switch, Divider, List, ListItemButton, ListItemText, useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import livechatApi from "./livechatApi";
import { PASTEL } from "./livechatTheme";

/**
 * DEV-1234 — "Level Akses Live Chat": khusus root-administrator & it-database (backend juga menolak
 * yang lain). Tiap level = daftar role + cakupan lihat + privilege. Hanya berlaku di brand customer;
 * corporate & mitra-purchasing memakai aturannya sendiri. Role yang tidak ada di level mana pun
 * memakai level default. Setiap perubahan langsung berlaku: semua agent memuat ulang hak aksesnya.
 */

const ASSIGNMENT_OPTIONS = [
    { value: "none", label: "Tidak", hint: "Hanya percakapan yang ditugaskan ke dirinya" },
    { value: "unassigned", label: "Yang belum ditugaskan", hint: "+ percakapan yang belum ditugaskan ke siapa pun" },
    { value: "all", label: "Semua", hint: "+ percakapan yang ditugaskan ke sales lain" },
];

const OWNER_OPTIONS = [
    { value: "mine", label: "Miliknya", hint: "Hanya customer yang owner Tele-nya dirinya" },
    { value: "mine_unowned", label: "+ tanpa owner", hint: "+ customer yang belum punya owner Tele" },
    { value: "all", label: "Semua", hint: "Customer milik siapa pun" },
];

const EMPTY_LEVEL = {
    key: "", name: "", description: "", isDefault: false, roles: [],
    assignmentScope: "unassigned", ownerScope: "mine_unowned", permissions: [],
};

const toSlug = (name) =>
    name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

/** Privilege yang dependensinya tidak terpenuhi (dipakai utk menonaktifkan checkbox & membuang saat simpan). */
const unmetReason = (def, level) => {
    if (def.requiresAssignmentScope && !def.requiresAssignmentScope.includes(level.assignmentScope)) {
        const labels = def.requiresAssignmentScope.map((v) => ASSIGNMENT_OPTIONS.find((o) => o.value === v)?.label || v);
        return `Butuh cakupan percakapan: ${labels.join(" / ")}`;
    }
    const missing = (def.requiresPermissions || []).filter((p) => !level.permissions.includes(p));
    if (missing.length > 0) return `Butuh privilege lain dicentang lebih dulu`;
    return null;
};

/** Buang privilege yang tidak valid lagi (mis. setelah cakupan diubah), berulang sampai stabil. */
const pruneOnce = (level, catalog) =>
    level.permissions.filter((key) => {
        const def = catalog.find((c) => c.key === key);
        return def && !unmetReason(def, level);
    });

const pruneInvalid = (level, catalog) => {
    const next = pruneOnce(level, catalog);
    return next.length === level.permissions.length ? level : pruneInvalid({ ...level, permissions: next }, catalog);
};

export const describeLevel = (level) => {
    if (level.assignmentScope === "none") return "Hanya melihat percakapan yang ditugaskan ke dirinya.";
    const assign = level.assignmentScope === "all"
        ? "semua percakapan, termasuk yang ditugaskan ke sales lain"
        : "percakapan yang ditugaskan ke dirinya dan yang belum ditugaskan";
    const owner = level.ownerScope === "mine"
        ? "hanya untuk customer miliknya (owner Tele)"
        : level.ownerScope === "mine_unowned" ? "untuk customer miliknya dan customer tanpa owner" : "untuk customer milik siapa pun";
    return `Melihat ${assign}, ${owner}. Percakapan yang ditugaskan ke dirinya selalu terlihat.`;
};

export default function AccessLevelDialog({ open, onClose }) {
    const isMobile = useMediaQuery("(max-width:900px)");
    const [data, setData] = useState(null);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [selectedKey, setSelectedKey] = useState(null);
    const [draft, setDraft] = useState(null);
    const [isNew, setIsNew] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        Promise.all([livechatApi.listAccessLevels(), livechatApi.listUserRoles()])
            .then(([levelsRes, rolesRes]) => {
                if (cancelled) return;
                const d = levelsRes.data?.data || {};
                setData(d);
                setRoles(rolesRes.data?.data || []);
                const first = (d.levels || []).find((l) => l.key === selectedKey) || (d.levels || [])[0];
                if (first && !isNew) {
                    setSelectedKey(first.key);
                    setDraft({ ...first });
                }
            })
            .catch((err) => {
                if (!cancelled) setError(err?.response?.data?.message || "Gagal memuat level akses");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // selectedKey/isNew sengaja tidak jadi dependency: pilihan level dipertahankan saat muat ulang
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, reloadKey]);

    const levels = useMemo(() => data?.levels || [], [data]);
    const catalog = useMemo(() => data?.permissions || [], [data]);
    const roleHolder = useMemo(() => {
        const map = {};
        levels.forEach((l) => l.roles.forEach((r) => { map[r] = l; }));
        return map;
    }, [levels]);
    const roleInfo = useMemo(() => Object.fromEntries(roles.map((r) => [r.slug, r])), [roles]);
    const groups = useMemo(() => [...new Set(catalog.map((c) => c.group))], [catalog]);

    const selectLevel = (level) => {
        setIsNew(false);
        setSelectedKey(level.key);
        setDraft({ ...level });
        setNotice(null);
        setError(null);
    };

    const startNew = () => {
        setIsNew(true);
        setSelectedKey(null);
        setDraft({ ...EMPTY_LEVEL });
        setNotice(null);
        setError(null);
    };

    const update = (changes) => setDraft((prev) => pruneInvalid({ ...prev, ...changes }, catalog));

    const togglePermission = (key, checked) =>
        update({ permissions: checked ? [...draft.permissions, key] : draft.permissions.filter((p) => p !== key) });

    // Role yang saat ini ada di level LAIN — saat disimpan, dikeluarkan dulu dari level asalnya.
    const movedRoles = draft ? draft.roles.filter((r) => roleHolder[r] && roleHolder[r].key !== draft.key) : [];

    const handleSave = async () => {
        if (!draft) return;
        setSaving(true);
        setError(null);
        setNotice(null);
        try {
            const sources = [...new Set(movedRoles.map((r) => roleHolder[r].key))];
            for (const sourceKey of sources) {
                const source = levels.find((l) => l.key === sourceKey);
                await livechatApi.updateAccessLevel(sourceKey, { roles: source.roles.filter((r) => !draft.roles.includes(r)) });
            }
            const payload = {
                name: draft.name,
                description: draft.description,
                roles: draft.roles,
                assignmentScope: draft.assignmentScope,
                ownerScope: draft.ownerScope,
                permissions: draft.permissions,
                ...(draft.isDefault ? { isDefault: true } : {}),
            };
            if (isNew) {
                const key = draft.key || toSlug(draft.name);
                await livechatApi.createAccessLevel({ ...payload, key });
                setIsNew(false);
                setSelectedKey(key);
            } else {
                await livechatApi.updateAccessLevel(draft.key, payload);
            }
            setNotice("Level disimpan. Agent yang sedang login otomatis memuat ulang hak aksesnya.");
            setReloadKey((k) => k + 1);
        } catch (err) {
            console.error("Error in AccessLevelDialog.handleSave:", err);
            setError(err?.response?.data?.message || "Gagal menyimpan level");
            setReloadKey((k) => k + 1);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        setError(null);
        try {
            await livechatApi.deleteAccessLevel(draft.key);
            setDeleteOpen(false);
            setSelectedKey(null);
            setDraft(null);
            setNotice(`Level "${draft.name}" dihapus. Role-nya kini memakai level default.`);
            setReloadKey((k) => k + 1);
        } catch (err) {
            console.error("Error in AccessLevelDialog.handleDelete:", err);
            setError(err?.response?.data?.message || "Gagal menghapus level");
            setDeleteOpen(false);
        } finally {
            setSaving(false);
        }
    };

    const handleMove = async (level, direction) => {
        const index = levels.findIndex((l) => l.key === level.key);
        const other = levels[index + direction];
        if (!other) return;
        setSaving(true);
        setError(null);
        try {
            await livechatApi.updateAccessLevel(level.key, { order: other.order });
            await livechatApi.updateAccessLevel(other.key, { order: level.order });
            setReloadKey((k) => k + 1);
        } catch (err) {
            setError(err?.response?.data?.message || "Gagal mengubah urutan");
        } finally {
            setSaving(false);
        }
    };

    const defaultLevel = levels.find((l) => l.isDefault);
    const unmapped = data?.unmappedRoles || [];
    const canSave = !!draft && !!draft.name.trim() && !saving;
    const userCountOf = (level) => level.roles.reduce((sum, r) => sum + (roleInfo[r]?.userCount || 0), 0);

    const levelList = (
        <Box sx={{ width: isMobile ? "100%" : 260, flexShrink: 0 }}>
            <List dense disablePadding sx={{ border: `1px solid ${PASTEL.border}`, borderRadius: 2, overflow: "hidden" }}>
                {levels.map((level, i) => (
                    <ListItemButton
                        key={level.key}
                        selected={!isNew && level.key === selectedKey}
                        onClick={() => selectLevel(level)}
                        sx={{ pr: 0.5, "&.Mui-selected": { backgroundColor: PASTEL.primarySoft } }}
                    >
                        <ListItemText
                            primary={
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{level.name}</Typography>
                                    {level.isDefault && <Chip size="small" label="default" sx={{ height: 18, fontSize: 10.5 }} />}
                                </Stack>
                            }
                            secondary={`${level.roles.length} role · ${userCountOf(level)} user`}
                        />
                        <Stack>
                            <IconButton size="small" aria-label={`Naikkan ${level.name}`} disabled={i === 0 || saving} onClick={(e) => { e.stopPropagation(); handleMove(level, -1); }}>
                                <ArrowUpwardRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton size="small" aria-label={`Turunkan ${level.name}`} disabled={i === levels.length - 1 || saving} onClick={(e) => { e.stopPropagation(); handleMove(level, 1); }}>
                                <ArrowDownwardRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Stack>
                    </ListItemButton>
                ))}
            </List>
            <Button fullWidth startIcon={<AddRoundedIcon />} onClick={startNew} sx={{ mt: 1, textTransform: "none" }}>
                Tambah Level
            </Button>
            {unmapped.length > 0 && (
                <Alert severity="info" sx={{ mt: 1, fontSize: 12.5 }}>
                    {unmapped.length} role belum dipetakan dan memakai level default
                    {defaultLevel ? ` "${defaultLevel.name}"` : ""}:{" "}
                    {unmapped.slice(0, 8).map((r) => r.slug).join(", ")}
                    {unmapped.length > 8 ? `, +${unmapped.length - 8} lainnya` : ""}
                </Alert>
            )}
        </Box>
    );

    const editor = draft && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                        label="Nama level" size="small" fullWidth required value={draft.name}
                        inputProps={{ maxLength: 60 }}
                        onChange={(e) => update({ name: e.target.value, ...(isNew ? { key: toSlug(e.target.value) } : {}) })}
                    />
                    {isNew && (
                        <TextField
                            label="Key" size="small" value={draft.key} sx={{ minWidth: 180 }}
                            helperText="Tidak bisa diubah setelah dibuat"
                            onChange={(e) => update({ key: toSlug(e.target.value) })}
                        />
                    )}
                </Stack>
                <TextField
                    label="Deskripsi" size="small" fullWidth value={draft.description || ""}
                    onChange={(e) => update({ description: e.target.value })}
                />

                <Autocomplete
                    multiple size="small" options={roles.map((r) => r.slug)} value={draft.roles}
                    onChange={(_e, value) => update({ roles: value })}
                    getOptionLabel={(slug) => slug}
                    renderOption={(props, slug) => {
                        const holder = roleHolder[slug];
                        return (
                            <li {...props} key={slug}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2">{slug} <Typography component="span" variant="caption" color="text.secondary">({roleInfo[slug]?.userCount ?? 0} user)</Typography></Typography>
                                    {holder && holder.key !== draft.key && (
                                        <Typography variant="caption" color="warning.main">di level "{holder.name}" — akan dipindahkan</Typography>
                                    )}
                                </Box>
                            </li>
                        );
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((slug, index) => (
                            <Chip {...getTagProps({ index })} key={slug} size="small" label={`${slug} ×${roleInfo[slug]?.userCount ?? 0}`} />
                        ))
                    }
                    renderInput={(params) => <TextField {...params} label="Role" placeholder="Cari role..." />}
                />
                {movedRoles.length > 0 && (
                    <Alert severity="warning" sx={{ fontSize: 13 }}>
                        Role berikut akan dipindahkan dari level asalnya saat disimpan:{" "}
                        {movedRoles.map((r) => `${r} (dari "${roleHolder[r].name}")`).join(", ")}
                    </Alert>
                )}

                <FormControl>
                    <FormLabel sx={{ fontSize: 13, fontWeight: 600 }}>Melihat percakapan yang bukan miliknya</FormLabel>
                    <RadioGroup row value={draft.assignmentScope} onChange={(e) => update({ assignmentScope: e.target.value })} aria-describedby="access-assignment-hint">
                        {ASSIGNMENT_OPTIONS.map((o) => (
                            <FormControlLabel key={o.value} value={o.value} control={<Radio size="small" />} label={<Typography variant="body2">{o.label}</Typography>} />
                        ))}
                    </RadioGroup>
                    <Typography id="access-assignment-hint" variant="caption" color="text.secondary">
                        {ASSIGNMENT_OPTIONS.find((o) => o.value === draft.assignmentScope)?.hint}
                    </Typography>
                </FormControl>
                <FormControl disabled={draft.assignmentScope === "none"}>
                    <FormLabel sx={{ fontSize: 13, fontWeight: 600 }}>Melihat customer berdasarkan owner Tele</FormLabel>
                    <RadioGroup row value={draft.ownerScope} onChange={(e) => update({ ownerScope: e.target.value })} aria-describedby="access-owner-hint">
                        {OWNER_OPTIONS.map((o) => (
                            <FormControlLabel key={o.value} value={o.value} control={<Radio size="small" />} label={<Typography variant="body2">{o.label}</Typography>} />
                        ))}
                    </RadioGroup>
                    <Typography id="access-owner-hint" variant="caption" color="text.secondary">
                        {draft.assignmentScope === "none"
                            ? "Tidak berlaku: level ini hanya melihat percakapan yang ditugaskan ke dirinya"
                            : OWNER_OPTIONS.find((o) => o.value === draft.ownerScope)?.hint}
                    </Typography>
                </FormControl>

                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Privilege</Typography>
                    {groups.map((group) => (
                        <Box key={group} sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{group}</Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                                {catalog.filter((c) => c.group === group).map((def) => {
                                    const checked = draft.permissions.includes(def.key);
                                    const reason = checked ? null : unmetReason(def, draft);
                                    return (
                                        <Box key={def.key} sx={{ display: "flex", flexDirection: "column", mb: 0.5 }}>
                                            <FormControlLabel
                                                sx={{ mb: -0.5 }}
                                                control={
                                                    <Checkbox
                                                        size="small" checked={checked} disabled={!!reason}
                                                        onChange={(e) => togglePermission(def.key, e.target.checked)}
                                                        inputProps={{ "aria-describedby": `perm-hint-${def.key}` }}
                                                    />
                                                }
                                                label={<Typography variant="body2">{def.label}</Typography>}
                                            />
                                            <Typography
                                                id={`perm-hint-${def.key}`} variant="caption"
                                                color={reason ? "warning.main" : "text.secondary"} sx={{ pl: 4, lineHeight: 1.3 }}
                                            >
                                                {reason || def.description}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}
                </Box>

                <FormControlLabel
                    control={<Switch checked={!!draft.isDefault} disabled={!isNew && levels.find((l) => l.key === draft.key)?.isDefault} onChange={(e) => update({ isDefault: e.target.checked })} />}
                    label={<Typography variant="body2">Level default (dipakai role yang belum dipetakan)</Typography>}
                />

                <Alert severity="info" icon={false} sx={{ fontSize: 13, backgroundColor: PASTEL.surfaceAlt }}>
                    {describeLevel(draft)}
                </Alert>
            </Stack>
        </Box>
    );

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={isMobile}>
                <DialogTitle sx={{ pr: 6 }}>
                    Level Akses Live Chat
                    <Typography variant="caption" display="block" color="text.secondary">
                        Berlaku untuk brand customer. Corporate & mitra memakai aturannya sendiri.
                    </Typography>
                    <IconButton onClick={onClose} aria-label="Tutup" sx={{ position: "absolute", right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                    {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
                    {loading && !data ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
                    ) : (
                        <Stack direction={isMobile ? "column" : "row"} spacing={2} divider={isMobile ? <Divider flexItem /> : null}>
                            {levelList}
                            {editor || (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Pilih level di kiri atau tambah level baru.</Typography>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
                    <span>
                        {draft && !isNew && !levels.find((l) => l.key === draft.key)?.isDefault && (
                            <Button color="error" onClick={() => setDeleteOpen(true)} disabled={saving} sx={{ textTransform: "none" }}>
                                Hapus Level
                            </Button>
                        )}
                    </span>
                    <Stack direction="row" spacing={1}>
                        <Button onClick={onClose} sx={{ textTransform: "none" }}>Tutup</Button>
                        <Button
                            variant="contained" onClick={handleSave} disabled={!canSave}
                            startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
                            sx={{ textTransform: "none", backgroundColor: PASTEL.primary, "&:hover": { backgroundColor: PASTEL.primaryDark } }}
                        >
                            {isNew ? "Buat Level" : "Simpan"}
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Hapus level "{draft?.name}"?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {draft?.roles.length || 0} role ({draft ? userCountOf(draft) : 0} user) akan memakai level default
                        {defaultLevel ? ` "${defaultLevel.name}"` : ""}. Perubahan langsung berlaku.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)} disabled={saving} sx={{ textTransform: "none" }}>Batal</Button>
                    <Button color="error" variant="contained" onClick={handleDelete} disabled={saving} sx={{ textTransform: "none" }}>Hapus</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
